import { useMemo, useRef, useState } from "react";

import type { PaymentIntent } from "@chargebee/chargebee-js-types";
import { LogSentryException, ServiceError, type ServiceErrorResponse } from "@shapr3d/frontend-lib";
import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseFormClearErrors, UseFormSetError } from "react-hook-form";
import { useLocation } from "react-router-dom";

import type { CheckoutFormData } from "../components/ChargebeeCheckout/ChargebeeCheckoutSchema";
import { localStorageKeys, queryKeys } from "../config";
import ChargebeeAdapter, {
  authorizePaymentIntent,
  ChargebeePaymentType,
} from "../lib/ChargebeeAdapter";
import { UMSrequestClient } from "../stores/requestClient";
import {
  CurrencySymbols,
  SearchParam,
  ServiceErrorCode,
  SubscriptionPeriod,
  SubscriptionTier,
  type SubscriptionType,
} from "../types";
import { isCountryInEU } from "../utils/countryUtils";
import type { DeepMapNativeObjectKeysToValue, OmitWithNever } from "../utils/typeUtils";
import { accountQueryKey } from "./useAccount";
import type { TeamsUserRole } from "./useAccountTypes";
import { useDebounce } from "./useDebounce";
import { TeamMembersQueryKey, TeamSubscriptionsQueryKey } from "./useTeams";

export type Estimate = {
  period: SubscriptionPeriod;
  amount: string;
  taxAmount: string;
  discountAmount: string;
  totalAmount: string;
  amountWithSubscriptionMigration?: string;
  currencyCode: string;
  trialEnd?: string;
  appliedCouponCode?: string;
  editorSeatCount: number;
  unitAmount: string;
};

export type CheckoutEstimateFormData = OmitWithNever<
  CheckoutFormData,
  "paymentType" | "card" | "inviteEmails" | "migrationConsent"
>;
export type BillingDetails = OmitWithNever<
  CheckoutEstimateFormData,
  "editorSeatCount" | "period" | "couponCode"
>;

export const formDataToEstimateFormData = (
  formData: CheckoutFormData
): CheckoutEstimateFormData => {
  const {
    paymentType: _paymentType,
    card: _card,
    inviteEmails: _inviteEmails,
    role: _role,
    migrationConsent: _migrationConsent,
    ...estimateFormData
  } = formData;
  return estimateFormData;
};

const estimateFormDataToBillingDetails = (formData: CheckoutEstimateFormData): BillingDetails => {
  let vatNumber = formData?.vatNumber;
  if (isCountryInEU(formData?.countryCode)) {
    vatNumber = vatNumber?.slice?.(2) ?? "";
  }
  const {
    period: _period,
    couponCode: _couponCode,
    editorSeatCount: _editorSeatCount,
    ...billingDetails
  } = { ...formData, vatNumber };

  billingDetails.countryCode = billingDetails.countryCode || "US";

  return Object.fromEntries(Object.entries(billingDetails).filter(([_, v]) => v != ""));
};

export const formDataToBillingDetails = (formData: CheckoutFormData): BillingDetails => {
  return estimateFormDataToBillingDetails(formDataToEstimateFormData(formData));
};

type CreateCheckoutMutationParams = {
  period: SubscriptionPeriod;
  tier?: SubscriptionTier;
};

export type CreateCheckoutRequestPayload = CreateCheckoutMutationParams & {
  forcePaddleBilling?: boolean;
};

export interface CreateChargebeeCheckoutResponseData {
  checkoutType?: never;
  id: string;
  billingDetails?: BillingDetails;
  trialEnd?: string;
  fixedPeriod?: SubscriptionPeriod;
  migratingFromType?: SubscriptionType;
  migrationAutoRenewEnabled?: boolean;
}

export interface PaddleBillingPrice {
  priceId: string;
  period: SubscriptionPeriod;
}

export interface CreateCheckoutPaddleResponseData {
  checkoutType: "paddle_one_time";
  paddleCustomerId?: string;
  prices: PaddleBillingPrice[];
}

export type CreateCheckoutResponse =
  | CreateChargebeeCheckoutResponseData
  | CreateCheckoutPaddleResponseData;

const createCheckout = async ({ period, tier }: CreateCheckoutMutationParams) => {
  const chinaPaddleBillingOverride =
    localStorage.getItem(localStorageKeys.chinaPaddleBillingOverride) === "true";

  const payload: CreateCheckoutRequestPayload = {
    period,
    ...(tier && { tier }),
    ...(chinaPaddleBillingOverride && { forcePaddleBilling: true }),
  };

  const jsonResponse = await UMSrequestClient.authenticatedRequest({
    method: "POST",
    url: `/checkout`,
    body: payload,
  });

  const response = await jsonResponse.json<CreateCheckoutResponse | ServiceError>();
  if (response instanceof ServiceError) {
    throw response;
  }

  return response;
};

export interface EstimateRequestPayload {
  couponCode?: string;
  billingDetails: BillingDetails;
  period: SubscriptionPeriod;
  editorSeatCount: number;
  requestVersion: number;
}

export interface EstimateMutationData {
  checkoutID: string;
  estimateFormData: CheckoutEstimateFormData;
}

export interface EstimateResponseData {
  estimate: Estimate;
  errors?: Array<{ code: ServiceErrorCode; message: string }>;
}

const updateEstimate = async ({
  estimateFormData,
  checkoutID,
}: EstimateMutationData): Promise<EstimateResponseData> => {
  const billingDetails = estimateFormDataToBillingDetails(estimateFormData);

  const period = estimateFormData.period;
  const editorSeatCount = estimateFormData.editorSeatCount;
  const couponCode = estimateFormData.couponCode;

  const response = await UMSrequestClient.authenticatedRequest({
    method: "POST",
    url: `/checkout/${checkoutID}/estimate`,
    body: {
      period,
      editorSeatCount,
      ...(couponCode && { couponCode }),
      billingDetails,
      requestVersion: Math.round(performance.now()),
    } satisfies EstimateRequestPayload,
  });

  const estimateResponse = await response.json<EstimateResponseData>();

  if (CurrencySymbols.get(estimateResponse.estimate.currencyCode) === undefined) {
    const error = new Error("unknown currency symbol from updateEstimate");
    LogSentryException({ messagePrefix: "Chargebee error", error, level: "info" });
    throw error;
  }
  return estimateResponse;
};

interface StartCheckoutParams {
  checkoutID: string;
  estimate: Estimate;
  formData: CheckoutFormData;
}

interface StartCheckoutRequestPayload {
  estimate: Estimate;
  billingDetails?: BillingDetails;
  paymentType: CheckoutFormData["paymentType"];
}

export interface StartCheckoutServiceErrorResponse extends ServiceErrorResponse {
  validationErrors?: DeepMapNativeObjectKeysToValue<StartCheckoutRequestPayload, { code: string }>;
}

interface StartCheckoutResponseData {
  paymentIntent: PaymentIntent;
}

const startCheckout = ({ checkoutID, estimate, formData }: StartCheckoutParams) => {
  const {
    card: _card,
    paymentType: _paymentType,
    inviteEmails: _inviteEmails,
    migrationConsent: _migrationConsent,
    ...estimateFormdata
  } = formData;
  const billingDetails = estimateFormDataToBillingDetails(estimateFormdata);

  const body: StartCheckoutRequestPayload = {
    estimate: estimate,
    billingDetails,
    paymentType: formData.paymentType,
  };

  return UMSrequestClient.authenticatedRequest({
    method: "POST",
    url: `/checkout/${checkoutID}/start`,
    body,
  }).then((x) => x.json<StartCheckoutResponseData>());
};

const finalizeCheckout = ({
  checkoutID,
  inviteEmails,
  inviteRole,
  authorizedPaymentIntentID,
}: {
  checkoutID: string;
  inviteEmails?: string[];
  inviteRole?: TeamsUserRole;
  authorizedPaymentIntentID: string;
}) => {
  return UMSrequestClient.authenticatedRequest({
    method: "POST",
    url: `/checkout/${checkoutID}/finalize`,
    body: {
      authorizedPaymentIntentID,
      ...(inviteEmails && {
        emails: inviteEmails,
        ...(inviteRole && { role: inviteRole }),
      }),
    },
  });
};

type CheckoutParams = {
  checkoutID: string;
  estimateResponseData: EstimateResponseData;
  formData: CheckoutFormData;
  simulatedCreditCardError?: string;
  queryClient: QueryClient;
};

const checkout = async ({
  checkoutID,
  estimateResponseData: { estimate, errors: estimateErrors },
  formData,
  simulatedCreditCardError,
  queryClient,
}: CheckoutParams) => {
  let couponCode = formData.couponCode;
  if (estimateErrors?.some((e) => e.code === ServiceErrorCode.InvalidCoupon) || couponCode === "") {
    couponCode = undefined;
  }

  const paymentIntent = (
    await startCheckout({ checkoutID, estimate, formData: { ...formData, couponCode } })
  ).paymentIntent;

  let authorizedPaymentIntent: PaymentIntent | null | undefined = null;
  if (formData.paymentType === ChargebeePaymentType.ApplePay) {
    authorizedPaymentIntent = await authorizePaymentIntent({
      paymentType: formData.paymentType,
      paymentIntent,
    });
  } else if (formData.paymentType === ChargebeePaymentType.Card) {
    if (!formData.card) {
      return Promise.reject(new Error("Card is null"));
    }

    authorizedPaymentIntent = await authorizePaymentIntent({
      paymentType: formData.paymentType,
      paymentIntent,
      card: formData.card,
      simulatedCreditCardError,
    });
  } else {
    return Promise.reject(new Error("Unknown payment type: " + formData.paymentType));
  }

  if (!authorizedPaymentIntent) throw new Error("Authorized payment intent is null");
  await finalizeCheckout({
    checkoutID,
    inviteEmails: formData.inviteEmails,
    inviteRole: formData.role,
    authorizedPaymentIntentID: authorizedPaymentIntent.id,
  });
  queryClient.invalidateQueries({ queryKey: accountQueryKey });
  queryClient.invalidateQueries({
    queryKey: TeamSubscriptionsQueryKey,
    refetchType: "all",
  });
  queryClient.invalidateQueries({
    queryKey: TeamMembersQueryKey,
    refetchType: "all",
  });
  return Promise.resolve();
};

export const EmptyCheckoutEstimateFormData: CheckoutEstimateFormData = {
  company: "",
  countryCode: "",
  city: "",
  zip: "",
  line1: "",
  line2: "",
  vatNumber: "",
  period: SubscriptionPeriod.Yearly,
  editorSeatCount: 1,
};

const validateVAT = async ({
  countryCode,
  vatNumber,
}: {
  countryCode: string;
  vatNumber: string;
}) => {
  if (!isCountryInEU(countryCode)) {
    return;
  }
  if (!vatNumber.startsWith(countryCode) && (countryCode != "GR" || !vatNumber.startsWith("EL"))) {
    return {
      error: "Invalid VAT number - Please include your country code",
    };
  }

  const vatNumberWithoutCountry = vatNumber.slice(2);
  if (vatNumberWithoutCountry === "") {
    return { error: "Invalid VAT number" };
  }

  try {
    const result = await ChargebeeAdapter.getInstance().validateVAT({
      countryCode,
      VATNumber: vatNumberWithoutCountry,
    });
    if (result?.status === "INVALID") {
      return { error: "Invalid VAT number" };
    }
  } catch (error) {
    if (error instanceof Error && error.message === "There were errors while submitting") {
      return { error: "Invalid VAT number" };
    }
    LogSentryException({ messagePrefix: "Chargebee VAT validation error", error });
    throw error;
  }
};

const estimateCacheQueryKey = ["estimateCache"];

export const useEstimate = ({
  checkoutID,
  setError,
  clearErrors,
}: {
  checkoutID: string | undefined;
  setError: UseFormSetError<CheckoutFormData>;
  clearErrors: UseFormClearErrors<CheckoutFormData>;
}) => {
  const queryClient = useQueryClient();
  const [isScheduled, setIsScheduled] = useState(false);
  const mutation = useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "estimate"],

    mutationFn: ({ estimateFormData }: { estimateFormData: CheckoutEstimateFormData }) =>
      updateEstimate({
        estimateFormData: estimateFormData ?? {},
        checkoutID: checkoutID ?? "",
      }),
    retry: false,
    onMutate: () => {
      setIsScheduled(false);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(estimateCacheQueryKey, data);
    },
  });
  const lastVAT = useRef<string | undefined>("");
  const lastCountryCode = useRef<string | undefined>("");
  const validateAndMutate: typeof mutation.mutate = async ({ estimateFormData }) => {
    if (
      lastVAT.current != estimateFormData?.vatNumber ||
      lastCountryCode.current != estimateFormData?.countryCode
    ) {
      lastVAT.current = estimateFormData.vatNumber;
      lastCountryCode.current = estimateFormData.countryCode;
      if (!estimateFormData?.vatNumber) {
        clearErrors("vatNumber");
      } else {
        const result = await validateVAT({
          countryCode: estimateFormData.countryCode ?? "",
          vatNumber: estimateFormData.vatNumber,
        });

        if (result?.error) {
          setError("vatNumber", { message: result.error });
          delete estimateFormData.vatNumber;
        } else {
          clearErrors("vatNumber");
        }
      }
    }
    mutation.mutate({ estimateFormData });
  };
  const debouncedMutate = useDebounce(validateAndMutate, 400);
  const mutate: typeof debouncedMutate = (...args) => {
    setIsScheduled(true);
    return debouncedMutate(...args);
  };
  mutate.flush = debouncedMutate.flush;
  mutate.cancel = debouncedMutate.cancel;

  const { data } = useQuery<typeof mutation.data>({
    queryKey: estimateCacheQueryKey,
    enabled: false,
  });

  return {
    ...mutation,
    data,
    mutate,
    isPending: mutation.isPending || isScheduled,
  };
};

export const useCreateCheckout = () => {
  return useMutation<CreateCheckoutResponse, Error | ServiceError, CreateCheckoutMutationParams>({
    mutationKey: [...queryKeys.authenticatedQueryKey, "createCheckout"],
    mutationFn: createCheckout,
  });
};

const useSimulatedCreditCardError = () => {
  const location = useLocation();
  const simulatedCreditCardError = useMemo(() => {
    const cardError = new URLSearchParams(location.search).get(SearchParam.CardError);

    if (typeof cardError !== "string" || cardError === "") {
      return undefined;
    }
    // For valid error codes see:
    // https://docs.adyen.com/development-resources/testing/result-code-testing/adyen-response-codes/
    return cardError;
  }, [location.search]);

  return simulatedCreditCardError;
};

export type SubmitCheckoutParams = Omit<CheckoutParams, "queryClient" | "simulatedCreditCardError">;

export const useSubmitCheckout = () => {
  const simulatedCreditCardError = useSimulatedCreditCardError();
  const queryClient = useQueryClient();

  return useMutation<void, Error, SubmitCheckoutParams>({
    mutationKey: [...queryKeys.authenticatedQueryKey, "checkout"],
    mutationFn: (params) =>
      checkout({
        ...params,
        ...(simulatedCreditCardError && { simulatedCreditCardError }),
        queryClient,
      }),
  });
};

export const useInitialPeriod = () => {
  const location = useLocation();

  return useMemo(
    () =>
      new URLSearchParams(location.search).get("period") === "monthly"
        ? SubscriptionPeriod.Monthly
        : SubscriptionPeriod.Yearly,
    [location.search]
  );
};

export const useInitialTier = () => {
  const location = useLocation();

  return useMemo(() => {
    const tier = new URLSearchParams(location.search).get(SearchParam.SubscriptionTier);
    return (tier as SubscriptionTier) ?? undefined;
  }, [location.search]);
};
